/**
 * Позиция оператора: сколько можно продать и какими долями.
 *
 * Модуль ЧИСТЫЙ — ни сети, ни React, ни времени изнутри, — потому что здесь
 * решается, СКОЛЬКО share уйдёт на биржу при нажатии «продать», и это правило
 * обязано проверяться тестом, а не разглядыванием компонента. То же разделение,
 * что у lib/quick.ts.
 *
 * # Почему число снимка нельзя брать как есть
 *
 * Размер в снимке приходит из цепочки (баланс ERC-1155), а когда узел молчит —
 * из Polymarket Data API, который индексирует блокчейн и отстаёт от факта
 * исполнения на секунды, а изредка на минуты. Между тем, что показывает снимок,
 * и тем, что реально доступно к продаже, лежат две наши собственные величины, о
 * которых он не знает ничего:
 *
 *   - НАШИ открытые ордера на продажу. Стоит в стакане GTC на 50 share из 120,
 *     оператор жмёт «100%» — и в стакане оказывается заявок на 170 против
 *     позиции в 120. Индексатор про наш ордер не знает в принципе, а мы знаем;
 *   - НАШИ продажи, случившиеся после снимка. Их индексатор ещё не увидел, и
 *     без вычета «100%» посчиталось бы от размера, которого уже нет.
 *
 * Обе вычитаются. НАШИ ПОКУПКИ после снимка, наоборот, НЕ прибавляются, и
 * асимметрия сознательная: ошибиться в сторону «продать меньше, чем можно»
 * стоит повторного клика через три секунды, ошибиться в сторону «продать
 * больше, чем есть» стоит отвергнутого биржей ордера в момент выхода.
 */

import { cmp, format, mul, parse, sub, toFixed, type Micro } from "./money";
import { resolveQuick } from "./quick";
import { floorToSizeStep } from "./size";
import type { Market, Order, Position, Side } from "../api/types";
import type { AssetBook } from "../store/book";
import type { FilledPosition } from "./fills";

/** Доли позиции, которыми продают. Три, потому что четвёртую не с чем сравнить. */
export const SELL_FRACTIONS = [25, 50, 100] as const;
export type SellFraction = (typeof SELL_FRACTIONS)[number];

/** Позиция по конкретному токену или null. */
export function positionOf(
  positions: readonly Position[] | undefined,
  assetId: string,
): Position | null {
  if (!positions || !assetId) return null;
  return positions.find((p) => p.asset_id === assetId) ?? null;
}

/**
 * Остаток НАШИХ открытых ордеров указанной стороны по токену, в share.
 *
 * PENDING считается наравне с LIVE: команда принята шиной и может исполниться в
 * любой момент, а не посчитать её значит разрешить вторую заявку на те же share.
 * То же направление осторожности, что у «судьба неизвестна» в store/orders.ts.
 *
 * `account` — резерв ОДНОГО аккаунта: продажа A01 не лежит на кошельке A03, и
 * вычитать её из позиции A03 значило бы недопродать там, где продавать можно.
 * Ордер без аккаунта (строка до бэкфилла) считается за каждым — в сторону
 * «продать меньше», как и всё в этом модуле. Без `account` — все наши ордера.
 */
export function reservedBy(
  orders: Record<string, Order>,
  assetId: string,
  side: Side,
  account?: string,
): string {
  let total = 0;
  for (const o of Object.values(orders)) {
    if (o.asset_id !== assetId || o.side !== side) continue;
    if (TERMINAL.has(o.status)) continue;
    if (account && o.account && o.account !== account) continue;
    const size = parse(o.size);
    const matched = parse(o.size_matched);
    if (size === null) continue;
    const rest = matched === null ? size : Math.max(0, size - matched);
    total += rest;
  }
  return format(total);
}

/** Зеркало store/orders.ts: статусы, из которых ордер не возвращается. */
const TERMINAL: ReadonlySet<Order["status"]> = new Set<Order["status"]>([
  "MATCHED",
  "CANCELED",
  "UNMATCHED",
  "REJECTED",
  "CANCELED_MARKET_RESOLVED",
  "INVALID",
]);

export interface Sellable {
  /** Сколько можно продать прямо сейчас, share. null — позиция неизвестна. */
  shares: string | null;
  /** Что показал снимок, до вычетов. null — позиция неизвестна. */
  indexed: string | null;
  /** Зарезервировано нашими открытыми ордерами на продажу. Вычтено. */
  reserved: string;
  /**
   * Чем обоснован размер.
   *
   * `snapshot` — снимком с сервера (балансом цепочки либо индексатором, см.
   * Position.source); `fills` — НАШИМ журналом сделок, когда снимок молчит;
   * null — обосновать нечем.
   *
   * Различие показывается оператору словами: продажа по журналу — это ответ на
   * вопрос «что я купил», а не «что лежит на кошельке», и знать, какой из двух
   * ответов сейчас на экране, он обязан.
   */
  basis: "snapshot" | "fills" | null;
}

/**
 * Сколько после нашей продажи снимок индексатора считается отставшим.
 *
 * Индексатор догоняет исполнение за секунды, изредка за минуты (2026-08-11 —
 * четыре); десять минут — с запасом. Дольше расхождение уже не отставание, а
 * позиция, набранная вне терминала, и верить надо снимку.
 */
export const SALE_LAG_MS = 10 * 60_000;

/**
 * Позиция по журналу вместо снимка индексатора — после нашей недавней продажи.
 *
 * Стенд multi 2026-10-07: публичный RPC пять минут не отвечал, снимок пришёл от
 * индексатора и ещё держал проданное. Журнал знал «0», но показ и план закрытия
 * брали журнал только когда он больше нуля, — строка показала 5.95, а второе
 * «закрыть» ушло на биржу и получило «balance: 0».
 *
 * Правило узкое: баланс цепочки (`fromChain`) точнее журнала и не подменяется,
 * а без недавней продажи снимок, показавший БОЛЬШЕ журнала, по-прежнему значит
 * позицию, набранную вне терминала (см. sellable).
 *
 * null — верить снимку; строка — позиция по журналу, "0" тоже ответ.
 */
export function journalAfterSale(input: {
  fromChain: boolean;
  filled: FilledPosition | null;
  now: number;
}): string | null {
  const { fromChain, filled, now } = input;
  if (fromChain || !filled || filled.lastSellAt <= 0) return null;
  if (now - filled.lastSellAt > SALE_LAG_MS) return null;
  return format(filled.shares > 0 ? filled.shares : 0);
}

/**
 * Сколько share доступно к продаже.
 *
 * `indexed === null` означает «позиция неизвестна» и обязано доехать до
 * интерфейса как null, а не как ноль: из нуля следует «продавать нечего», из
 * неизвестности — «не знаю, кнопки выключены».
 *
 * # Откат на наши филлы
 *
 * `filled` — позиция, посчитанная по НАШЕМУ журналу сделок (lib/fills.ts). Она
 * берётся ровно тогда, когда снимок не дал ничего: он неизвестен либо равен
 * нулю, а филлы говорят, что позиция есть.
 *
 * Разбор 2026-08-11: индексатор молчал четыре минуты, пока 319.492429 share
 * лежали на кошельке (подтверждено балансом ERC-1155), и всё это время продажа
 * была недоступна — размер ордера брался только из снимка. Ошибка «нечего
 * продавать» при непустой позиции дороже, чем ошибка в обратную сторону: во
 * втором случае биржа отвергнет ордер по балансу, в первом оператор не выйдет
 * из позиции вовсе.
 *
 * Обратной подмены не бывает: снимок, показавший размер БОЛЬШЕ нашего журнала
 * (позиция набрана не через терминал), остаётся как есть.
 */
export function sellable(input: {
  indexed: string | null;
  /** Позиция по нашим филлам, share. null — филлов по токену не было. */
  filled?: string | null;
  orders: Record<string, Order>;
  assetId: string;
  /** Чья позиция: резерв вычитается только его (см. reservedBy). */
  account?: string;
  /** Журнал после нашей недавней продажи (journalAfterSale); null — нет такой. */
  afterSale?: string | null;
}): Sellable {
  const reserved = reservedBy(input.orders, input.assetId, "SELL", input.account);
  const held = parse(reserved);
  const snapshot = input.indexed === null ? null : parse(input.indexed);
  const ours = input.filled == null ? null : parse(input.filled);
  const sold = input.afterSale == null ? null : parse(input.afterSale);

  // После нашей продажи отставший снимок индексатора не продаёт проданное:
  // берётся меньшее из снимка и журнала (journalAfterSale).
  if (sold !== null && held !== null && (snapshot === null || sold < snapshot)) {
    const left = sub(sold, held);
    return {
      shares: format(left === null || left < 0 ? 0 : left),
      indexed: input.indexed,
      reserved,
      basis: "fills",
    };
  }

  // Из снимка исходим всегда, когда он есть и непустой; иначе — из журнала.
  const useFills = (snapshot === null || snapshot <= 0) && ours !== null && ours > 0;
  const have = useFills ? ours : snapshot;
  if (have === null || held === null) {
    return { shares: null, indexed: input.indexed, reserved, basis: null };
  }

  const left = sub(have, held);
  // Зажим нулём: резерв способен превысить снимок, когда индексатор отстал, а
  // ордер уже стоит. Отрицательный «доступный размер» — это не долг, это ноль.
  return {
    shares: format(left === null || left < 0 ? 0 : left),
    indexed: input.indexed,
    reserved,
    basis: useFills ? "fills" : "snapshot",
  };
}

/**
 * Доля позиции в share, округлённая ВНИЗ до 0.01.
 *
 * null — продавать нечего: доля не набирает даже минимального шага размера.
 *
 * 100 % тоже округляется вниз, и это не мелочь: индексатор отдаёт размеры до
 * шести знаков, а биржа принимает два, поэтому «продать всё» физически означает
 * «продать 19.23 из 19.234567». Округлить вверх значило бы отправить ордер на
 * размер, которого нет, и получить отказ по балансу целиком.
 */
export function sizeForFraction(shares: string, percent: SellFraction): string | null {
  const total = parse(shares);
  if (total === null || total <= 0) return null;

  // Через money.mul, а не total * percent / 100: Number-арифметика запрещена, а
  // произведение на позиции в тысячи share уходит за 2⁵³ — mul уводит такое в
  // BigInt-ветку сам.
  const raw = percent === 100 ? total : mul(total, percent * 10_000);
  if (raw === null) return null;

  const size = floorToSizeStep(raw);
  return size > 0 ? format(size) : null;
}

/**
 * Непродаваемый хвост позиции: то, что меньше шага размера.
 *
 * Пустая строка — хвоста нет. Показывается один раз под чипами: оператор,
 * нажавший «100%» и увидевший остаток, обязан знать, что это не недоработка
 * кнопки, а точность, которую принимает биржа.
 */
export function dust(shares: string): string {
  const total = parse(shares);
  if (total === null || total <= 0) return "";
  const rest = total - floorToSizeStep(total);
  return rest > 0 ? format(rest) : "";
}

/**
 * Чем плоха эта доля на этом рынке. null — годна.
 *
 * Формулировка дословно повторяет resolveQuick и checkAgainstMarket: два
 * описания одного правила, сказанные по-разному, разъезжаются в первый же день,
 * и оператор перестаёт понимать, кому верить.
 *
 * Размер НЕ поднимается до минимума — он именно отвергается. Подъём продал бы
 * больше, чем оператор попросил, а это та же ошибка, ради которой размер из
 * суммы округляется вниз.
 */
export function fractionProblem(
  shares: string,
  percent: SellFraction,
  minOrderSize: string,
): string | null {
  const size = sizeForFraction(shares, percent);
  if (size === null) {
    return `${percent}% от ${shares} share — меньше 0.01 share, такой ордер не собрать`;
  }
  const min = parse(minOrderSize);
  const got = parse(size);
  if (min === null || min <= 0 || got === null) return null;
  if (cmp(got, min) < 0) {
    return (
      `${percent}% от ${shares} — это ${size} share, меньше минимального ` +
      `размера рынка (${format(min)})`
    );
  }
  return null;
}

/**
 * Сколько принесёт ПРОДАЖА `size` share по цене `price`, в долларах, ПОСЛЕ
 * комиссии.
 *
 * (цена − средняя) × размер − комиссия. Средняя приходит из индексатора и уже
 * взвешена по всем нашим покупкам, поэтому FIFO здесь не нужен: на бинарном
 * исходе все share одинаковы, и «какие именно» продаются — вопрос без содержания.
 *
 * Считается ПО ЦЕНЕ КНОПКИ, а не по cur_price позиции, и это главное. cur_price
 * — оценка индексатора, отставшая на секунды; кнопка же уходит по конкретному
 * уровню стакана, и мейкер с тейкером в одну и ту же секунду дают разные деньги.
 * Показать одно число на обеих кнопках значило бы соврать как минимум на одной.
 *
 * # Комиссия
 *
 * Вычитается РОВНО ОДИН РАЗ, и это проверено: `avg_price` индексатора комиссии не
 * содержит (сверено на позиции с двумя покупками — сумма затрат совпала с сырыми
 * нотионалами, а не с ними плюс комиссия), поэтому двойного вычета здесь быть не
 * может. Само число считает вызывающий: комиссия зависит от РОЛИ, а роль знает
 * панель, а не эта функция (`lib/fees.ts`, `resolveQuick`). У мейкера она ноль.
 *
 * `fee` отсутствует — прибыль ДО комиссии; так считается погашение расчётного
 * рынка (`positionPnl`), где никакой сделки не происходит. Отличать «комиссии
 * нет» от «комиссию не сообщили» обязан вызывающий: `null` в расчёте комиссии
 * означает незнакомое расписание рынка, и показывать его нулём нельзя.
 *
 * null — считать не из чего, и это НЕ ноль. Средняя ноль или отрицательна —
 * такого входа не было: индексатор ещё не увидел покупку, и разность «цена − 0»
 * выдала бы всю выручку за прибыль ровно в тот момент, когда её и проверяют.
 */
export function sellPnl(input: {
  price: string;
  size: string;
  avgPrice: string;
  /** Комиссия продажи в долларах. Отсутствует — прибыль до комиссии. */
  fee?: string;
}): Micro | null {
  const price = parse(input.price);
  const size = parse(input.size);
  const avg = parse(input.avgPrice);
  if (price === null || size === null || avg === null) return null;
  if (avg <= 0 || size <= 0) return null;
  const diff = sub(price, avg);
  if (diff === null) return null;
  const gross = mul(diff, size);
  if (gross === null || input.fee === undefined) return gross;
  const fee = parse(input.fee);
  // Неразобранная комиссия — это НЕ ноль: показать грязную прибыль там, где
  // просили чистую, значит завысить её молча. Такого входа не бывает (строку
  // формирует lib/fees.ts), и если он появился — пусть будет виден прочерком.
  if (fee === null) return null;
  return sub(gross, fee);
}

/**
 * Итог по позиции целиком, в долларах: (цена расчёта − средняя) × размер.
 *
 * ТОЛЬКО для разрешившегося рынка. На живом окне `cur_price` брать нельзя — см.
 * `positionValue` ниже, где разобрано почему.
 *
 * После расчёта рынка `cur_price` становится 0 или 1, и та же формула даёт
 * результат погашения. Это единственное состояние, в котором число индексатора
 * не может устареть: оно окончательное.
 *
 * Комиссия здесь НЕ вычитается, и это не упущение: погашение расчётного рынка
 * комиссии не платит вовсе — никакой сделки не происходит.
 */
export function positionPnl(position: Position): Micro | null {
  return sellPnl({
    price: position.cur_price,
    size: position.size,
    avgPrice: position.avg_price,
  });
}

/** Сколько стоит позиция и чем эта цена обоснована. */
export type PositionValue =
  | {
      ok: true;
      /** Цена, по которой посчитан итог. */
      price: string;
      /** Итог в долларах; null — средняя цена входа неизвестна. */
      pnl: Micro | null;
      /**
       * Чем обоснована цена: `book` — тейкерским выходом по стакану,
       * `settled` — ценой расчёта разрешившегося рынка.
       */
      basis: "book" | "settled";
    }
  | { ok: false; reason: string };

/**
 * Сколько стоит позиция ПРЯМО СЕЙЧАС.
 *
 * # Почему не cur_price индексатора
 *
 * Потому что на коротких окнах он не движется вовсе. Замер 13.08.2026 на трёх
 * живых окнах подряд: BTC 5m — стакан прошёл 0.265 → 0.315 за двадцать секунд,
 * `curPrice` остался 0.495; ETH 5m — 0.185 против 0.465; BTC 15m — 0.425 против
 * Up 0.485 / Down 0.515. Это снимок момента около открытия окна, когда обе
 * стороны ≈0.5, и дальше он не обновляется.
 *
 * Прежняя формулировка «оценка индексатора, отставшая на секунды» была
 * добросовестным, но неверным допущением: она не отстаёт, она стоит. Считанный
 * по ней итог систематически врал ЗНАКОМ — читал позицию так, будто окно
 * вернулось к монетке. На скриншоте оператора строка показывала −$1.74 там, где
 * кнопки продажи в той же секунде показывали +$1.37 и +$1.22.
 *
 * # Почему именно тейкерский выход
 *
 * Число обязано совпадать с кнопкой «Taker», а не просто «быть из стакана»: два
 * пнл на одном экране, посчитанных по-разному, — это ровно тот дефект, который
 * здесь и чинится. Поэтому цена берётся тем же `resolveQuick`, которым живёт
 * кнопка, и комиссия вычитается той же. Формально это «лучшая покупка минус
 * шаг» с тейкерской комиссией, а не проход по лесенке: обещать выход по объёму,
 * которого мы не проверяли, всё так же нельзя.
 *
 * # Почему отказ — это слова, а не число
 *
 * Оценка, которую нельзя реализовать, обязана называться. Пустая сторона
 * покупок, размер меньше минимума рынка, неподтверждённый стакан — у каждого
 * своя причина, и `resolveQuick` формулирует их сам. Подставить вместо них
 * цифру значило бы вернуться к тому, с чего начали.
 */
export function positionValue(input: {
  position: Position;
  market: Market;
  book: AssetBook | undefined;
}): PositionValue {
  const { position, market, book } = input;

  if (position.redeemable) {
    return {
      ok: true,
      price: position.cur_price,
      pnl: positionPnl(position),
      basis: "settled",
    };
  }

  const resolved = resolveQuick(
    { side: "SELL", mode: "cross" },
    { market, assetId: position.asset_id, book, shares: position.size },
  );
  if (!resolved.ok) return { ok: false, reason: resolved.reason };

  // Комиссия — та же, что на кнопке, и вычитается ровно один раз: avg_price
  // индексатора её не содержит (сверено на позиции с двумя покупками).
  //
  // Незнакомое расписание рынка даёт ПРОЧЕРК, а не грязную прибыль: показать
  // число до комиссии там, где обещано после, значит завысить его молча — тот
  // самый дефект, который закрыла веха M7. Кнопка продажи поступает так же.
  const pnl =
    resolved.fee === null
      ? null
      : sellPnl({
          price: resolved.price,
          size: resolved.size,
          avgPrice: position.avg_price,
          fee: resolved.fee,
        });

  return { ok: true, price: resolved.price, pnl, basis: "book" };
}

/**
 * Деньги со знаком: «+$0.31», «−$0.12», «$0».
 *
 * Минус — типографский U+2212, а не дефис: в моноширинной колонке дефис не
 * отличается от переноса, а знак у прибыли — это первое, что читают.
 *
 * ЦЕНТЫ, а не шесть знаков. Прибыль — производная величина: (цена − средняя) ×
 * размер, и микро-хвост в ней от точности индексатора, а не от денег, которые
 * кто-то заплатит. «+$5.902039» на кнопке занимает вдвое больше места, чем
 * «+$5.90», и ломает её в две строки ради цифр, которых нет ни в одном
 * платеже. Точное значение остаётся в подсказке.
 */
export function formatSigned(m: Micro): string {
  if (m === 0) return "$0";
  return `${m > 0 ? "+" : "−"}$${toFixed(Math.abs(m), 2)}`;
}
